import { useEffect, useRef, useState } from 'react'
import { Tooltip } from './Tooltip'
import { ExternalLink, GripVertical } from 'lucide-react'
import { HandRaisedIcon } from './HandRaisedIcon'
import { ScrollChevron, TabScrollEdge } from './TabScrollEdge'
import { useHorizontalScrollNav } from './useHorizontalScrollNav'

// ─────────────────────────────────────────────────────────────────────────────
// SearchTabs — shared multi-tab search switcher used by agentic-hub + load-hub.
//
// Unlike the generic <Tabs>, search tabs are a stateful collection the user
// can add/remove. Each tab represents an in-flight search and shows:
//   • a vertical route marker (origin dot → dashed line → destination dot)
//   • a primary label (origin) + secondary label (destination, or "Anywhere")
//   • optional inline badges (pending questions, unread emails, total load count)
//   • a close × button on hover (or a spinner when the tab is mid-delete)
//
// Two visual variants are supported because agentic-hub and load-hub use
// different shapes today:
//   • 'browser' — chrome-style tab pills with rounded-top, used by agentic-hub
//     to communicate "each tab is a workspace". Active = white surface bleeding
//     down into the content below; inactive = muted slate-100 cap.
//   • 'pill' — single-row flat pills used by load-hub when the tab strip lives
//     above the filter form.
//
// The component also renders a "+" button at the end and an optional
// "Close all" affordance with two-step confirmation when there are 2+ tabs.
// ─────────────────────────────────────────────────────────────────────────────

export interface SearchTabItem {
    /** Stable id used for selection + remove callbacks. */
    id: string
    /** Primary label (typically origin city or "New Search"). */
    primary: string
    /** Optional secondary line (typically destination or "Anywhere"). */
    secondary?: string
    /** Optional load count to display as a soft-accent badge after the labels. */
    count?: number
    /** Optional pending-question count — amber pulsing badge. */
    pendingCount?: number
    /** Optional unread-email count — blue badge. */
    unreadCount?: number
    /** When true, replace the × with a spinner (used while a delete request is in flight). */
    isDeleting?: boolean
    /** Optional owner chip rendered before the labels — a one-letter avatar in a stable colour.
     *  Supplied only when the distinction is meaningful (AI Hub passes it when a dispatcher has more
     *  than one email connected); omitted, nothing renders and the tab is unchanged. */
    marker?: { initial: string; colorClass: string; title: string }
}

interface SearchTabsProps {
    tabs: SearchTabItem[]
    activeTabId: string | null
    onSelectTab: (tabId: string) => void
    /** When omitted, no × button renders on individual tabs. */
    onRemoveTab?: (tabId: string) => void
    onAddTab: () => void
    /** When supplied AND there are 2+ tabs, a "Close all" affordance appears at the end. */
    onCloseAllTabs?: () => void
    /** When true, the "+" button shows a spinner instead of the plus icon. */
    isCreating?: boolean
    /** 'browser' = chrome-tab shape (default). 'pill' = single-row flat pills. */
    variant?: 'browser' | 'pill'
    /** When supplied, each tab grows a grip handle and can be drag-reordered; called with the full new
     *  id order on drop. Omitted → tabs are fixed (the default for existing consumers). */
    onReorderTabs?: (orderedIds: string[]) => void
    /** When supplied, double-clicking a tab opens an inline rename input; called with the new name on
     *  commit (Enter/blur; Esc cancels). An empty name is the caller's "clear custom name" signal. */
    onRenameTab?: (tabId: string, label: string) => void
    /** Optional escape hatch for the active workspace. Rendered as a persistent, touch-friendly action on
     *  the active tab; omitted for inactive tabs so callers never export page-owned state for the wrong tab. */
    onOpenActiveTab?: (tabId: string) => void
    className?: string
}

export const SearchTabs = ({
    tabs,
    activeTabId,
    onSelectTab,
    onRemoveTab,
    onAddTab,
    onCloseAllTabs,
    isCreating = false,
    variant = 'browser',
    onReorderTabs,
    onRenameTab,
    onOpenActiveTab,
    className = '',
}: SearchTabsProps) => {
    const { scrollerRef, canScrollLeft, canScrollRight, scrollByStep } = useHorizontalScrollNav()
    // Pointer-based drag-reorder (only armed when onReorderTabs is supplied). NOT HTML5 drag-and-drop:
    // dragstart from an element inside a <button> nested in an overflow-x scroller is unreliable across
    // browsers (and untestable via synthetic input) — pointer capture works everywhere. The grip starts
    // the drag; on every move the tab under the pointer becomes the insertion point (live reorder — after
    // the insert the hovered element IS the dragged tab, so the loop is self-stabilising).
    const [draggingId, setDraggingId] = useState<string | null>(null)
    const draggingRef = useRef<string | null>(null)
    const tabsRef = useRef(tabs)
    tabsRef.current = tabs
    const dragCleanupRef = useRef<(() => void) | null>(null)
    useEffect(() => () => dragCleanupRef.current?.(), [])
    const startReorder = (id: string) => (e: React.PointerEvent) => {
        if (!onReorderTabs) return
        e.preventDefault()
        draggingRef.current = id
        setDraggingId(id)
        const onMove = (ev: PointerEvent) => {
            const dragging = draggingRef.current
            if (!dragging) return
            const targetId = (document.elementFromPoint(ev.clientX, ev.clientY)?.closest('[data-searchtab-id]') as HTMLElement | null)?.dataset.searchtabId
            if (!targetId || targetId === dragging) return
            const currentIds = tabsRef.current.map((t) => t.id)
            const from = currentIds.indexOf(dragging)
            const to = currentIds.indexOf(targetId)
            if (from === -1 || to === -1) return
            // Splice at the target's ORIGINAL index: moving right this lands AFTER the target (its index
            // shifted down by the removal), moving left it lands before — i.e. the dragged tab takes the
            // target's slot in both directions. Inserting "before target" naively was a no-op rightwards.
            const ids = currentIds.filter((x) => x !== dragging)
            ids.splice(to, 0, dragging)
            onReorderTabs(ids)
        }
        const onUp = () => {
            draggingRef.current = null
            setDraggingId(null)
            window.removeEventListener('pointermove', onMove)
            window.removeEventListener('pointerup', onUp)
            window.removeEventListener('pointercancel', onUp)
            dragCleanupRef.current = null
        }
        window.addEventListener('pointermove', onMove)
        window.addEventListener('pointerup', onUp)
        window.addEventListener('pointercancel', onUp)
        dragCleanupRef.current = onUp
    }

    return (
        // Row layout: only the tabs scroll (left region); the controls — right
        // chevron, "+", "Close all" — live in a fixed cluster on the right so
        // they're always visible and never run off-screen behind each other.
        <div className={`flex items-stretch bg-white dark:bg-slate-900 ${variant === 'browser' ? 'h-9' : 'h-11 min-h-[44px]'} ${className}`}>
            {/* Scrollable tabs region. position:relative anchors the left fade/chevron,
                which only appears once the user has scrolled right. */}
            <div className="relative min-w-0 flex-1">
                <div
                    ref={scrollerRef}
                    className={`flex h-full items-center overflow-x-auto scrollbar-hide ${variant === 'browser' ? 'px-2' : 'px-3'}`}
                    style={{ scrollbarWidth: 'none' }}
                >
                    {tabs.map((tab) => (
                        <SearchTab
                            key={tab.id}
                            tab={tab}
                            isActive={tab.id === activeTabId}
                            onSelect={() => onSelectTab(tab.id)}
                            onRemove={onRemoveTab ? () => onRemoveTab(tab.id) : undefined}
                            variant={variant}
                            reorderable={!!onReorderTabs}
                            isDragging={draggingId === tab.id}
                            onGripPointerDown={startReorder(tab.id)}
                            onRename={onRenameTab ? (label) => onRenameTab(tab.id, label) : undefined}
                            onOpenInBrowser={tab.id === activeTabId && onOpenActiveTab ? () => onOpenActiveTab(tab.id) : undefined}
                        />
                    ))}

                    {/* "+" scrolls with the tabs, right after the last one (original spot). */}
                    <AddTabButton onClick={onAddTab} isCreating={isCreating} variant={variant} />
                </div>

                {canScrollLeft && <TabScrollEdge side="left" visible disabled={false} onScroll={() => scrollByStep('left')} />}
            </div>

            {/* Control cluster — scroll chevron + close-all, always reachable. */}
            <div className={`flex shrink-0 items-center gap-1 ${variant === 'browser' ? 'px-2' : 'px-3'}`}>
                <ScrollChevron side="right" disabled={!canScrollRight} onScroll={() => scrollByStep('right')} />
                {onCloseAllTabs && tabs.length >= 2 && <CloseAllButton onConfirm={onCloseAllTabs} />}
            </div>
        </div>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// Individual tab
// ─────────────────────────────────────────────────────────────────────────────

interface SearchTabProps {
    tab: SearchTabItem
    isActive: boolean
    onSelect: () => void
    onRemove?: () => void
    variant: 'browser' | 'pill'
    /** Drag-reorder wiring (armed only when the strip received onReorderTabs). */
    reorderable?: boolean
    isDragging?: boolean
    onGripPointerDown?: (e: React.PointerEvent) => void
    /** Inline-rename commit (armed only when the strip received onRenameTab). Double-click opens the editor. */
    onRename?: (label: string) => void
    /** Open this active workspace in a top-level browser tab. */
    onOpenInBrowser?: () => void
}

const SearchTab = ({ tab, isActive, onSelect, onRemove, variant, reorderable, isDragging, onGripPointerDown, onRename, onOpenInBrowser }: SearchTabProps) => {
    const [editing, setEditing] = useState(false)
    const [draft, setDraft] = useState('')
    // Esc guard: cancelling unmounts a focused input, whose blur would otherwise still run commit().
    const cancelledRef = useRef(false)
    // Only an EDITED field commits — an untouched double-click + click-away must not freeze the current
    // derived label as a custom name. Tracked as "did they type", NOT as "did the text change": deliberately
    // retyping the derived label to pin it (so it survives a lane change) is a real rename.
    const dirtyRef = useRef(false)
    const startEditing = () => {
        if (!onRename) return
        cancelledRef.current = false
        dirtyRef.current = false
        setDraft(tab.primary)
        setEditing(true)
    }
    const commit = () => {
        if (cancelledRef.current) return
        cancelledRef.current = true // one-shot: Enter's commit also swallows the unmount blur that follows
        setEditing(false)
        if (dirtyRef.current) onRename?.(draft)
    }
    const cancel = () => {
        cancelledRef.current = true
        setEditing(false)
    }
    const shapeClasses =
        variant === 'browser'
            ? `flex items-center gap-2 h-7 pl-2.5 ${onRemove && onOpenInBrowser ? 'pr-14' : onRemove || onOpenInBrowser ? 'pr-8' : 'pr-2.5'} rounded-t-md text-left transition-all whitespace-nowrap border border-b-0 ${
                  isActive
                      ? 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm'
                      : 'bg-slate-100 dark:bg-slate-800/60 border-transparent hover:bg-slate-100 dark:hover:bg-slate-800'
              }`
            : // Reserve right-side space for the absolute-positioned close button when present,
              // otherwise its overlay would overlap unread/pending badges.
              `relative flex items-center gap-2.5 pl-3 ${onRemove && onOpenInBrowser ? 'pr-14' : onRemove || onOpenInBrowser ? 'pr-8' : 'pr-3'} py-2 transition-opacity hover:opacity-90`

    return (
        <div className={`relative group flex-shrink-0 flex items-end ${isDragging ? 'opacity-40' : ''}`} data-searchtab-id={reorderable ? tab.id : undefined}>
            <button
                type="button"
                onClick={onSelect}
                onDoubleClick={startEditing}
                className={shapeClasses}
                aria-selected={isActive}
                role="tab"
                title={onRename ? 'Double-click to rename' : undefined}
            >
                {reorderable && (
                    <span
                        onPointerDown={onGripPointerDown}
                        onClick={(e) => e.stopPropagation()}
                        // Two quick grab attempts must not bubble into the tab's dblclick rename editor.
                        onDoubleClick={(e) => e.stopPropagation()}
                        title="Drag to reorder"
                        aria-label="Drag to reorder tab"
                        // touch-none: without it, mobile/touch drags scroll the strip instead of moving the tab.
                        className="-ml-1 shrink-0 cursor-grab touch-none text-slate-300 hover:text-slate-500 active:cursor-grabbing dark:text-slate-600 dark:hover:text-slate-400"
                    >
                        <GripVertical size={13} />
                    </span>
                )}
                <RouteMarker isActive={isActive} />

                {tab.marker && (
                    // Real tooltip, not the native `title`: the tab button carries its own title
                    // ("Double-click to rename"), and a one-letter chip is useless until you can read
                    // the address behind it — a browser tooltip's ~1s delay is long enough that most
                    // people never see it. `bottom` so it does not cover the tab strip above.
                    <Tooltip title={tab.marker.title} placement="bottom">
                        <span
                            className={`shrink-0 flex items-center justify-center h-[15px] w-[15px] -ml-0.5 mr-0.5 rounded-full text-white text-[9px] font-bold leading-none ${
                                tab.marker.colorClass
                            } ${isActive ? '' : 'opacity-60'}`}
                            aria-label={tab.marker.title}
                        >
                            {tab.marker.initial}
                        </span>
                    </Tooltip>
                )}

                <div className="flex flex-col leading-[1.15] min-w-0 max-w-[140px] mr-3">
                    <span
                        className={`text-[11px] font-semibold truncate ${
                            isActive ? 'text-slate-900 dark:text-slate-100' : 'text-slate-500 dark:text-slate-400'
                        }`}
                        title={tab.primary}
                    >
                        {tab.primary}
                    </span>
                    {tab.secondary && (
                        <span
                            className={`text-[10px] truncate ${isActive ? 'text-slate-500 dark:text-slate-400' : 'text-slate-400 dark:text-slate-500'}`}
                            title={tab.secondary}
                        >
                            {tab.secondary}
                        </span>
                    )}
                </div>

                <span className="shrink-0 flex items-center gap-0.5 ml-0.5">
                    {typeof tab.count === 'number' && tab.count > 0 && (
                        <span
                            className={`inline-flex items-center justify-center h-[18px] min-w-[24px] px-1.5 rounded-full text-[10px] font-semibold tabular-nums ${
                                isActive
                                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                            }`}
                        >
                            {tab.count.toLocaleString('en-US')}
                        </span>
                    )}
                    {typeof tab.pendingCount === 'number' && tab.pendingCount > 0 && (
                        // Hand-raised icon — same glyph LoadRow uses for its
                        // pending-question pill, so the affordance reads consistently
                        // between the tab strip and individual row chrome.
                        <span
                            className="inline-flex items-center gap-0.5 h-[14px] px-1 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 text-[9px] font-semibold tabular-nums animate-pulse"
                            title={`${tab.pendingCount} pending question${tab.pendingCount > 1 ? 's' : ''}`}
                        >
                            <HandRaisedIcon />
                            {tab.pendingCount}
                        </span>
                    )}
                    {typeof tab.unreadCount === 'number' && tab.unreadCount > 0 && (
                        <span
                            className="inline-flex items-center gap-0.5 h-[14px] px-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[9px] font-semibold tabular-nums"
                            title={`${tab.unreadCount} unread email${tab.unreadCount > 1 ? 's' : ''}`}
                        >
                            <svg className="h-2.5 w-2.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                                />
                            </svg>
                            {tab.unreadCount}
                        </span>
                    )}
                </span>
            </button>

            {/* Inline rename editor — overlaid on the tab (an <input> can't live INSIDE the tab <button>:
                nested interactive content is invalid and steals its key/click events). Enter/blur commit,
                Esc cancels; an emptied name commits '' = the caller's clear-custom-name signal. */}
            {editing && (
                <input
                    type="text"
                    value={draft}
                    onChange={(e) => {
                        dirtyRef.current = true
                        setDraft(e.target.value)
                    }}
                    onBlur={commit}
                    onKeyDown={(e) => {
                        // Keep Enter/Esc local — a page-level key handler (drawer close, form submit)
                        // must not also fire off a tab rename keystroke.
                        if (e.key === 'Enter' || e.key === 'Escape') e.stopPropagation()
                        if (e.key === 'Enter') commit()
                        if (e.key === 'Escape') cancel()
                    }}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                    onFocus={(e) => e.target.select()}
                    aria-label="Tab name"
                    className="absolute inset-x-1 top-1/2 z-10 h-6 -translate-y-1/2 rounded-md border border-blue-400 bg-white px-1.5 text-[11px] font-semibold text-slate-900 outline-none dark:border-blue-500 dark:bg-slate-900 dark:text-slate-100"
                />
            )}

            {/* Close × — positioned absolutely so it doesn't shift the layout on hover.
                Sized at h-4.5/w-4.5 with an h-3 svg + right-2 gap so the × has breathing
                room from the tab title and counts (the previous h-3.5/w-3.5 + h-2 icon
                at right-1.5 felt cramped next to the title). */}
            {onRemove &&
                (tab.isDeleting ? (
                    <span
                        className={`absolute ${variant === 'browser' ? 'top-[9px] right-2' : 'top-1/2 -translate-y-1/2 right-2'} h-[18px] w-[18px] flex items-center justify-center pointer-events-none`}
                    >
                        <svg className="h-3 w-3 animate-spin text-slate-500 dark:text-slate-400" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                    </span>
                ) : (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation()
                            onRemove()
                        }}
                        className={`absolute ${variant === 'browser' ? 'top-[9px] right-2' : 'top-1/2 -translate-y-1/2 right-2'} h-[18px] w-[18px] rounded flex items-center justify-center opacity-0 group-hover:opacity-100 text-slate-500 dark:text-slate-400 hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400 transition-all`}
                        aria-label="Close tab"
                    >
                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                ))}

            {onOpenInBrowser && (
                <button
                    type="button"
                    onClick={(event) => {
                        event.stopPropagation()
                        onOpenInBrowser()
                    }}
                    onDoubleClick={(event) => event.stopPropagation()}
                    className={`absolute ${variant === 'browser' ? 'top-1' : 'top-1/2 -translate-y-1/2'} ${onRemove ? 'right-7' : 'right-1'} flex size-7 items-center justify-center rounded-md text-accent transition-colors hover:bg-accent-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent`}
                    aria-label={`Open ${tab.primary} in browser tab`}
                    title="Open in browser tab"
                >
                    <ExternalLink size={14} strokeWidth={2} />
                </button>
            )}

            {/* Pill variant draws its active underline at the bottom of the row */}
            {variant === 'pill' && isActive && <span className="absolute inset-x-2 -bottom-px h-[2px] bg-blue-600 rounded-t-sm" />}
        </div>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

const RouteMarker = ({ isActive }: { isActive: boolean }) => {
    // Active = blue accent (origin dot empty-circle, destination dot filled), inactive = slate.
    // The dashed connector uses an inline style — Tailwind doesn't render border-left
    // dashed cleanly at 1.5px on a zero-width element.
    return (
        <div className="flex flex-col items-center gap-0.5 shrink-0 py-0.5">
            <span className={`w-[5px] h-[5px] rounded-full border-[1.5px] ${isActive ? 'border-blue-600' : 'border-slate-400 dark:border-slate-500'}`} />
            <span
                className="w-0 h-1.5"
                style={{
                    borderLeft: `1.5px dashed ${isActive ? 'rgb(37 99 235)' : 'rgb(148 163 184)'}`,
                }}
            />
            <span className={`w-[5px] h-[5px] rounded-full ${isActive ? 'bg-blue-600' : 'bg-slate-400 dark:bg-slate-500'}`} />
        </div>
    )
}

interface AddTabButtonProps {
    onClick: () => void
    isCreating: boolean
    variant: 'browser' | 'pill'
}

const AddTabButton = ({ onClick, isCreating, variant }: AddTabButtonProps) => {
    const sizeClasses = variant === 'browser' ? 'h-7 w-7 ml-1 rounded-lg' : 'h-7 w-7 ml-2 rounded-md'
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={isCreating}
            className={`flex-shrink-0 flex items-center justify-center ${sizeClasses} bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors disabled:opacity-50 disabled:pointer-events-none`}
            aria-label="New tab"
            title="New tab"
        >
            {isCreating ? (
                <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
            ) : (
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
            )}
        </button>
    )
}

export const CloseAllButton = ({ onConfirm }: { onConfirm: () => void }) => {
    const [confirming, setConfirming] = useState(false)

    if (confirming) {
        return (
            <div className="flex items-center gap-1 ml-1">
                <span className="text-xs text-red-600 dark:text-red-400 whitespace-nowrap">Close all?</span>
                <button
                    type="button"
                    onClick={() => {
                        onConfirm()
                        setConfirming(false)
                    }}
                    className="h-5 px-1.5 rounded text-xs font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
                >
                    Yes
                </button>
                <button
                    type="button"
                    onClick={() => setConfirming(false)}
                    className="h-5 px-1.5 rounded text-xs font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                    No
                </button>
            </div>
        )
    }

    return (
        <button
            type="button"
            onClick={() => setConfirming(true)}
            className="ml-1 h-7 px-2 rounded-lg text-xs text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-all whitespace-nowrap"
            title="Close all tabs"
        >
            Close all
        </button>
    )
}
